import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { chartOfAccountsTemplateContract } from "@common/contracts/chart-of-accounts-template.contract";
import {
    ChartOfAccountsTemplate,
    ChartOfAccountsTemplateSchema,
    ChartOfAccountsTemplateListItem,
    ChartOfAccountsTemplateListItemSchema,
} from "@common/types/chartOfAccountsTemplate";
import {
    ChartOfAccountsTemplateAccount,
    ChartOfAccountsTemplateAccountGroup,
    ChartOfAccountsTemplateAccountGroupSchema,
    ChartOfAccountsTemplateAccountSchema,
} from "@common/types/domains/accounting/chartOfAccountsTemplateAccount";
import {
    ChartOfAccountsTemplateApplyResponse,
    ChartOfAccountsTemplateApplyResponseSchema,
    ChartOfAccountsTemplatePreviewResponseSchema,
    ChartOfAccountsTemplateAppliedEntitiesResponseSchema,
} from "@common/schemas/chart-of-accounts-template-apply.schema";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { CSV_MIME } from "@lib/fileDownload";
import { invalidateAccountRelatedQueries } from "./account.api";

const chartOfAccountsTemplateClient = getBackendAPIClient(
    chartOfAccountsTemplateContract,
);

const chartOfAccountsTemplateBlobClient = getBackendAPIClient(
    chartOfAccountsTemplateContract,
    "blob",
);

export async function getChartOfAccountsTemplateCsv({
    organizationId,
    chartOfAccountsTemplateId,
}: {
    organizationId: string;
    chartOfAccountsTemplateId: string;
}) {
    return await chartOfAccountsTemplateBlobClient.exportChartOfAccountsTemplateCsv(
        {
            params: { organizationId, chartOfAccountsTemplateId },
            extraHeaders: { Accept: CSV_MIME },
        },
    );
}

export const chartOfAccountsTemplateQueryKeys = {
    all: () => ["chart-of-accounts-template"] as const,
    organization: (organizationId: string) =>
        [
            ...chartOfAccountsTemplateQueryKeys.all(),
            "organization",
            organizationId,
        ] as const,
    list: (organizationId: string, includeArchived = false) =>
        [
            ...chartOfAccountsTemplateQueryKeys.organization(organizationId),
            "list",
            { includeArchived },
        ] as const,
    accounts: (organizationId: string, templateId: string) =>
        [
            ...chartOfAccountsTemplateQueryKeys.organization(organizationId),
            "accounts",
            templateId,
        ] as const,
    previewApply: (
        organizationId: string,
        templateId: string,
        body: ServerInferRequest<
            typeof chartOfAccountsTemplateContract.previewApplyChartOfAccountsTemplate
        >["body"],
    ) =>
        [
            ...chartOfAccountsTemplateQueryKeys.organization(organizationId),
            "preview-apply",
            templateId,
            body,
        ] as const,
    appliedEntities: (organizationId: string, templateId: string) =>
        [
            ...chartOfAccountsTemplateQueryKeys.organization(organizationId),
            "applied-entities",
            templateId,
        ] as const,
};

export interface ChartOfAccountsTemplateAccountsData {
    accounts: ChartOfAccountsTemplateAccount[];
    groups: ChartOfAccountsTemplateAccountGroup[];
}

function parseTemplateAccountsResponse(body: {
    accounts: ChartOfAccountsTemplateAccount[];
    groups: ChartOfAccountsTemplateAccountGroup[];
}): ChartOfAccountsTemplateAccountsData {
    return {
        accounts: body.accounts.map((account) =>
            ChartOfAccountsTemplateAccountSchema.parse(account),
        ),
        groups: body.groups.map((group) =>
            ChartOfAccountsTemplateAccountGroupSchema.parse(group),
        ),
    };
}

export function useChartOfAccountsTemplates(options?: {
    enabled?: boolean;
    includeArchived?: boolean;
}) {
    const { organizationId } = useScope();
    const isEnabled = options?.enabled ?? true;
    const includeArchived = options?.includeArchived ?? false;

    return useQuery({
        queryKey: organizationId
            ? chartOfAccountsTemplateQueryKeys.list(
                  organizationId,
                  includeArchived,
              )
            : [],
        queryFn: async () => {
            if (!organizationId) {
                return [] as ChartOfAccountsTemplateListItem[];
            }

            const response =
                await chartOfAccountsTemplateClient.listChartOfAccountsTemplates(
                    {
                        params: { organizationId },
                        query: includeArchived ? { includeArchived } : {},
                    },
                );

            return response.body.chartOfAccountsTemplates.map((t) =>
                ChartOfAccountsTemplateListItemSchema.parse(t),
            );
        },
        enabled: !!organizationId && isEnabled,
    });
}

export function useCreateChartOfAccountsTemplateMutation() {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof chartOfAccountsTemplateContract.createChartOfAccountsTemplate
            >["body"],
        ) => {
            if (!organizationId) {
                throw new Error("No active organization");
            }

            const response =
                await chartOfAccountsTemplateClient.createChartOfAccountsTemplate(
                    { params: { organizationId }, body },
                );

            return ChartOfAccountsTemplateSchema.parse(response.body);
        },
        onSuccess: () => {
            if (!organizationId) {
                return;
            }
            void queryClient.invalidateQueries({
                queryKey:
                    chartOfAccountsTemplateQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}

export function useChartOfAccountsTemplateAccounts({
    templateId,
    enabled = true,
}: {
    templateId?: string;
    enabled?: boolean;
}) {
    const { organizationId } = useScope();

    return useQuery({
        queryKey:
            organizationId && templateId
                ? chartOfAccountsTemplateQueryKeys.accounts(
                      organizationId,
                      templateId,
                  )
                : [],
        queryFn: async (): Promise<ChartOfAccountsTemplateAccountsData> => {
            if (!organizationId || !templateId) {
                return { accounts: [], groups: [] };
            }

            const response =
                await chartOfAccountsTemplateClient.getChartOfAccountsTemplateAccounts(
                    {
                        params: {
                            organizationId,
                            chartOfAccountsTemplateId: templateId,
                        },
                    },
                );

            return parseTemplateAccountsResponse(response.body);
        },
        enabled: !!organizationId && !!templateId && enabled,
    });
}

export function useSaveChartOfAccountsTemplateAccountsMutation(
    chartOfAccountsTemplate?: Pick<ChartOfAccountsTemplate, "id">,
) {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof chartOfAccountsTemplateContract.saveChartOfAccountsTemplateAccounts
            >["body"],
        ): Promise<ChartOfAccountsTemplateAccountsData> => {
            if (!organizationId || !chartOfAccountsTemplate) {
                throw new Error("Missing context for template accounts save");
            }

            const response =
                await chartOfAccountsTemplateClient.saveChartOfAccountsTemplateAccounts(
                    {
                        params: {
                            organizationId,
                            chartOfAccountsTemplateId:
                                chartOfAccountsTemplate.id,
                        },
                        body,
                    },
                );

            return parseTemplateAccountsResponse(response.body);
        },
        onSuccess: () => {
            if (!organizationId || !chartOfAccountsTemplate) {
                return;
            }
            void queryClient.invalidateQueries({
                queryKey: chartOfAccountsTemplateQueryKeys.accounts(
                    organizationId,
                    chartOfAccountsTemplate.id,
                ),
            });
        },
    });
}

export function useUpdateChartOfAccountsTemplateMutation(
    chartOfAccountsTemplate?: Pick<ChartOfAccountsTemplate, "id">,
) {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof chartOfAccountsTemplateContract.updateChartOfAccountsTemplate
            >["body"],
        ) => {
            if (!organizationId || !chartOfAccountsTemplate) {
                throw new Error("Missing context for template update");
            }

            const response =
                await chartOfAccountsTemplateClient.updateChartOfAccountsTemplate(
                    {
                        params: {
                            organizationId,
                            chartOfAccountsTemplateId:
                                chartOfAccountsTemplate.id,
                        },
                        body,
                    },
                );

            return ChartOfAccountsTemplateSchema.parse(response.body);
        },
        onSuccess: () => {
            if (!organizationId) {
                return;
            }
            void queryClient.invalidateQueries({
                queryKey:
                    chartOfAccountsTemplateQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}

// Server-computed merge preview for applying a template to one or more
// entities. POST-as-query (mirrors `usePreviewCloseTemplateQuery`): the body is
// part of the cache key so switching the selected entities refetches.
export function usePreviewApplyCoaTemplateQuery(
    templateId: string | null,
    body: ServerInferRequest<
        typeof chartOfAccountsTemplateContract.previewApplyChartOfAccountsTemplate
    >["body"],
) {
    const { organizationId } = useScope();
    const hasEntities = body.entityIds.length > 0;
    const isEnabled = !!organizationId && !!templateId && hasEntities;

    return useQuery({
        queryKey:
            organizationId && templateId
                ? chartOfAccountsTemplateQueryKeys.previewApply(
                      organizationId,
                      templateId,
                      body,
                  )
                : [],
        queryFn: async () => {
            if (!organizationId || !templateId) {
                throw new Error("Missing preview context");
            }
            const response =
                await chartOfAccountsTemplateClient.previewApplyChartOfAccountsTemplate(
                    {
                        params: {
                            organizationId,
                            chartOfAccountsTemplateId: templateId,
                        },
                        body,
                    },
                );
            if (response.status !== 200) {
                const errorBody = response.body;
                const errorMessage =
                    "message" in errorBody &&
                    typeof errorBody.message === "string"
                        ? errorBody.message
                        : "Failed to preview template";
                throw new Error(errorMessage);
            }
            return ChartOfAccountsTemplatePreviewResponseSchema.parse(
                response.body,
            );
        },
        enabled: isEnabled,
    });
}

// Applies the template to one or more entities (mirrors
// `useApplyCloseTemplateMutation`). Throws on a non-201 so the per-entity apply
// loop can surface partial failures. On success, invalidates the templates-list
// query (so the BE-6 `appliedEntitiesCount` refreshes) and the live CoA queries
// for the applied entities (so a user viewing the entity's Chart of Accounts
// sees the newly created/updated accounts).
export function useApplyCoaTemplateMutation(templateId?: string) {
    const queryClient = useQueryClient();
    const { organizationId, workspaceId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof chartOfAccountsTemplateContract.applyChartOfAccountsTemplate
            >["body"],
        ): Promise<ChartOfAccountsTemplateApplyResponse> => {
            if (!organizationId || !templateId) {
                throw new Error("Missing context");
            }
            const response =
                await chartOfAccountsTemplateClient.applyChartOfAccountsTemplate(
                    {
                        params: {
                            organizationId,
                            chartOfAccountsTemplateId: templateId,
                        },
                        body,
                    },
                );
            if (response.status !== 201) {
                const errorBody = response.body;
                const errorMessage =
                    "message" in errorBody &&
                    typeof errorBody.message === "string"
                        ? errorBody.message
                        : "Failed to apply template";
                throw new Error(errorMessage);
            }
            return ChartOfAccountsTemplateApplyResponseSchema.parse(
                response.body,
            );
        },
        onSuccess: (_data, variables) => {
            if (organizationId) {
                void queryClient.invalidateQueries({
                    queryKey:
                        chartOfAccountsTemplateQueryKeys.organization(
                            organizationId,
                        ),
                });
            }
            invalidateAccountRelatedQueries({
                entityIds: variables.entityIds,
                workspaceId,
            });
        },
    });
}

export function useChartOfAccountsTemplateAppliedEntities(
    templateId: string | null,
) {
    const { organizationId } = useScope();
    const isEnabled = !!organizationId && !!templateId;

    return useQuery({
        queryKey:
            organizationId && templateId
                ? chartOfAccountsTemplateQueryKeys.appliedEntities(
                      organizationId,
                      templateId,
                  )
                : [],
        queryFn: async () => {
            if (!organizationId || !templateId) {
                return { appliedEntities: [] };
            }
            const response =
                await chartOfAccountsTemplateClient.listAppliedEntitiesForChartOfAccountsTemplate(
                    {
                        params: {
                            organizationId,
                            chartOfAccountsTemplateId: templateId,
                        },
                    },
                );
            if (response.status !== 200) {
                throw new Error("Failed to load applied entities");
            }
            return ChartOfAccountsTemplateAppliedEntitiesResponseSchema.parse(
                response.body,
            );
        },
        enabled: isEnabled,
    });
}

export function useArchiveChartOfAccountsTemplateMutation() {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (chartOfAccountsTemplateId: string) => {
            if (!organizationId) {
                throw new Error("Missing context for template archive");
            }

            const response =
                await chartOfAccountsTemplateClient.archiveChartOfAccountsTemplate(
                    { params: { organizationId, chartOfAccountsTemplateId } },
                );
            if (response.status !== 200) {
                throw new Error("Failed to archive template");
            }

            return ChartOfAccountsTemplateSchema.parse(response.body);
        },
        onSuccess: () => {
            if (!organizationId) {
                return;
            }
            void queryClient.invalidateQueries({
                queryKey:
                    chartOfAccountsTemplateQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}

export function useEnableChartOfAccountsTemplateMutation() {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (chartOfAccountsTemplateId: string) => {
            if (!organizationId) {
                throw new Error("Missing context for template enable");
            }

            const response =
                await chartOfAccountsTemplateClient.unarchiveChartOfAccountsTemplate(
                    { params: { organizationId, chartOfAccountsTemplateId } },
                );
            if (response.status !== 200) {
                throw new Error("Failed to enable template");
            }

            return ChartOfAccountsTemplateSchema.parse(response.body);
        },
        onSuccess: () => {
            if (!organizationId) {
                return;
            }
            void queryClient.invalidateQueries({
                queryKey:
                    chartOfAccountsTemplateQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}

export function useDeleteChartOfAccountsTemplateMutation(
    chartOfAccountsTemplate?: Pick<ChartOfAccountsTemplate, "id">,
) {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async () => {
            if (!organizationId || !chartOfAccountsTemplate) {
                throw new Error("Missing context for template delete");
            }

            await chartOfAccountsTemplateClient.deleteChartOfAccountsTemplate({
                params: {
                    organizationId,
                    chartOfAccountsTemplateId: chartOfAccountsTemplate.id,
                },
            });
        },
        onSuccess: () => {
            if (!organizationId) {
                return;
            }
            void queryClient.invalidateQueries({
                queryKey:
                    chartOfAccountsTemplateQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}
